import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  LinearProgress,
  IconButton,
  Card,
  CardContent,
  Fade,
  Slide,
  TextField,
  Avatar,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Work as WorkIcon,
  Language as LanguageIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  Create as CreateIcon,
  AccountBalance as AccountBalanceIcon,
  Computer as ComputerIcon,
  Engineering as EngineeringIcon,
  LocalHospital as LocalHospitalIcon,
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  Telegram as TelegramIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  VolumeUp as VolumeUpIcon,
  VideoCall as VideoCallIcon,
  Translate as TranslateIcon,
  Subtitles as SubtitlesIcon,
  MicExternalOn as MicExternalOnIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Article as ArticleIcon,
  Timer as TimerIcon,
  Star as StarIcon,
  PlayArrow as PlayArrowIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const OnboardingModal = ({ open, onSave, user = null }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [profession, setProfession] = useState('');
  const [otherProfession, setOtherProfession] = useState('');
  const [source, setSource] = useState('');
  const [otherSource, setOtherSource] = useState('');
  const [goals, setGoals] = useState({
    speechToText: false,
    textToSpeech: false,
    videoCreation: false,
    translation: false,
    subtitles: false,
    dictation: false,
  });
  const [showOtherProfessionInput, setShowOtherProfessionInput] = useState(false);
  const [showOtherSourceInput, setShowOtherSourceInput] = useState(false);
  const [slideDirection, setSlideDirection] = useState('left');

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const totalSteps = user?.remaining_seconds == 300 ? 4 : 3;

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setSlideDirection('left');
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setSlideDirection('right');
      setCurrentStep(currentStep - 1);
    }
  };

  const handleProfessionSelect = (value) => {
    setProfession(value);
    if (value === 'other') {
      setShowOtherProfessionInput(true);
    } else {
      setShowOtherProfessionInput(false);
      // Auto-advance after 300ms delay
      setTimeout(() => {
        handleNext();
      }, 300);
    }
  };

  const handleSourceSelect = (value) => {
    setSource(value);
    if (value === 'other') {
      setShowOtherSourceInput(true);
    } else {
      setShowOtherSourceInput(false);
      // Auto-advance after 300ms delay
      setTimeout(() => {
        handleNext();
      }, 300);
    }
  };

  const handleGoalToggle = (goalName) => {
    setGoals(prev => ({
      ...prev,
      [goalName]: !prev[goalName]
    }));
  };

  const getStartDestination = () => {
    if (goals.textToSpeech && !goals.speechToText) {
      return '/text-to-speech';
    }
    return '/new';
  };

  const handleStart = (destination = getStartDestination()) => {
    const collectedData = {
      onboarding: {
        profession: profession === 'other' ? otherProfession.trim() : profession,
        source: source === 'other' ? otherSource.trim() : source,
        goals: Object.keys(goals).filter(key => goals[key]),
      },
      onboardingCompleted: true,
    };
    onSave(collectedData);
    navigate(destination);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return profession !== '' && (profession !== 'other' || otherProfession.trim() !== '');
      case 1:
        return source !== '' && (source !== 'other' || otherSource.trim() !== '');
      case 2:
        return Object.values(goals).some(v => v);
      case 3:
        return true;
      default:
        return true;
    }
  };

  const professionOptions = [
    { value: 'journalist', label: 'Լրագրող', icon: <LanguageIcon /> },
    { value: 'sociologist', label: 'Սոցիոլոգ', icon: <SchoolIcon /> },
    { value: 'marketing', label: 'Մարքեթոլոգ', icon: <BusinessIcon /> },
    { value: 'content_creator', label: 'Կոնտենտ ստեղծող', icon: <CreateIcon /> },
    { value: 'finance', label: 'Ֆինանսիստ', icon: <AccountBalanceIcon /> },
    { value: 'it', label: 'ՏՏ մասնագետ', icon: <ComputerIcon /> },
    { value: 'teacher', label: 'Ուսուցիչ', icon: <SchoolIcon /> },
    { value: 'doctor', label: 'Բժիշկ', icon: <LocalHospitalIcon /> },
    { value: 'student', label: 'Ուսանող', icon: <SchoolIcon /> },
    { value: 'other', label: 'Այլ', icon: <WorkIcon /> },
  ];

  const sourceOptions = [
    { value: 'google', label: 'Google', icon: <GoogleIcon /> },
    { value: 'chatgpt', label: 'ChatGPT', icon: <ComputerIcon /> },
    { value: 'telegram', label: 'Telegram', icon: <TelegramIcon /> },
    { value: 'facebook', label: 'Facebook', icon: <FacebookIcon /> },
    { value: 'instagram', label: 'Instagram', icon: <InstagramIcon /> },
    { value: 'youtube', label: 'YouTube', icon: <YouTubeIcon /> },
    { value: 'recommendation', label: 'Ընկերոջ խորհուրդ', icon: <PeopleIcon /> },
    { value: 'news', label: 'Լրատվամիջոցներ', icon: <ArticleIcon /> },
    { value: 'other', label: 'Այլ', icon: <SearchIcon /> },
  ];

  const goalOptions = [
    { name: 'speechToText', label: 'Ձայնը դարձնել տեքստ', icon: <RecordVoiceOverIcon /> },
    { name: 'textToSpeech', label: 'Տեքստը դարձնել ձայն', icon: <VolumeUpIcon /> },
    { name: 'videoCreation', label: 'Ստեղծել վիդեո', icon: <VideoCallIcon /> },
    { name: 'integration', label: 'Ինտեգրել զանգերի կենտրոնի հետ', icon: <ComputerIcon /> },
    { name: 'subtitles', label: 'Ստեղծել ենթագրեր', icon: <SubtitlesIcon /> },
  ];

  const stepTitles = [
    'Ո՞րն է Ձեր մասնագիտությունը',
    'Ինչպե՞ս իմացաք մեր մասին',
    'Ի՞նչ եք ցանկանում փորձարկել',
    'Փորձարկեք անվճար'
  ];

  const stepSubtitles = [
    'Եկեք ծանոթանանք, ինչո՞վ եք զբաղվում',
    'Դա մեզ կօգնի ավելի շատ մարդկանց տեղեկացնել մեր մասին',
    'Ընտրեք գործառույթները',
    'Ձեր անվճար ժամանակը սպասում է'
  ];

  const hasSpeechToTextInterest = goals.speechToText;
  const hasTextToSpeechInterest = goals.textToSpeech;
  const hasBothSpeechInterests = hasSpeechToTextInterest && hasTextToSpeechInterest;

  const freeCredits = [];

  if (hasSpeechToTextInterest || (!hasSpeechToTextInterest && !hasTextToSpeechInterest)) {
    freeCredits.push({
      id: 'stt',
      icon: <TimerIcon sx={{ color: theme.palette.success.main, fontSize: '2rem' }} />,
      title: '5 րոպե անվճար',
      description: 'Մենք տալիս ենք 5 րոպե անվճար փորձարկման ժամանակ։ Դուք կարող եք փորձարկել բոլոր գործառույթները և տեսնել արդյունքները:',
      route: '/new',
      actionLabel: 'Փորձել խոսքից տեքստը',
      titleColor: theme.palette.success.main,
      background: `linear-gradient(135deg, ${theme.palette.success.main}15, ${theme.palette.info.main}15)`,
      borderColor: `${theme.palette.success.main}30`
    });
  }

  if (hasTextToSpeechInterest) {
    freeCredits.push({
      id: 'tts',
      icon: <VolumeUpIcon sx={{ color: theme.palette.primary.main, fontSize: '2rem' }} />,
      title: '10 անվճար TTS գեներացիա',
      description: 'Դուք ստանում եք 10 անվճար TTS գեներացիա, որպեսզի փորձարկեք ձայները և գնահատեք որակը ձեր բովանդակության վրա։',
      route: '/text-to-speech',
      actionLabel: 'Փորձել տեքստից ձայնը',
      titleColor: theme.palette.primary.main,
      background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
      borderColor: `${theme.palette.primary.main}30`
    });
  }

  const ProfessionSourceChip = ({ option, selected, onClick }) => (
    <Chip
      icon={option.icon}
      label={option.label}
      onClick={onClick}
      variant={selected ? "filled" : "outlined"}
      sx={{
        height: 44,
        fontSize: '0.9rem',
        fontWeight: selected ? 600 : 400,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        background: selected 
          ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
          : 'transparent',
        color: selected ? 'white' : theme.palette.text.primary,
        borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: theme.shadows[2],
        },
        '& .MuiChip-icon': {
          color: selected ? 'white' : theme.palette.text.secondary,
        },
        minWidth: isMobile ? '140px' : '160px',
        justifyContent: 'flex-start',
        px: 1,
      }}
    />
  );

  const GoalChip = ({ option, selected, onClick }) => (
    <Chip
      icon={option.icon}
      label={option.label}
      onClick={onClick}
      variant={selected ? "filled" : "outlined"}
      sx={{
        height: 48,
        fontSize: '0.95rem',
        fontWeight: selected ? 600 : 400,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        background: selected 
          ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
          : 'transparent',
        color: selected ? 'white' : theme.palette.text.primary,
        borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: theme.shadows[3],
        },
        '& .MuiChip-icon': {
          color: selected ? 'white' : theme.palette.text.secondary,
        },
        minWidth: isMobile ? '140px' : '160px',
        justifyContent: 'flex-start',
        px: 1,
      }}
    />
  );

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1.5,
              justifyContent: 'center',
              maxWidth: '100%'
            }}>
              {professionOptions.map(option => (
                <ProfessionSourceChip
                  key={option.value}
                  option={option}
                  selected={profession === option.value}
                  onClick={() => handleProfessionSelect(option.value)}
                />
              ))}
            </Box>
            {showOtherProfessionInput && (
              <TextField
                autoFocus
                fullWidth
                label="Նշեք Ձեր ոլորտը"
                variant="outlined"
                value={otherProfession}
                onChange={(e) => setOtherProfession(e.target.value)}
                sx={{ mt: 2, maxWidth: 400 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && otherProfession.trim()) {
                    handleNext();
                  }
                }}
              />
            )}
          </Box>
        );
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1.5,
              justifyContent: 'center',
              maxWidth: '100%'
            }}>
              {sourceOptions.map(option => (
                <ProfessionSourceChip
                  key={option.value}
                  option={option}
                  selected={source === option.value}
                  onClick={() => handleSourceSelect(option.value)}
                />
              ))}
            </Box>
            {showOtherSourceInput && (
              <TextField
                autoFocus
                fullWidth
                label="Նշեք աղբյուրը"
                variant="outlined"
                value={otherSource}
                onChange={(e) => setOtherSource(e.target.value)}
                sx={{ mt: 2, maxWidth: 400 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && otherSource.trim()) {
                    handleNext();
                  }
                }}
              />
            )}
          </Box>
        );
      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1.5,
              justifyContent: 'center',
              maxWidth: '100%'
            }}>
              {goalOptions.map(option => (
                <GoalChip
                  key={option.name}
                  option={option}
                  selected={goals[option.name]}
                  onClick={() => handleGoalToggle(option.name)}
                />
              ))}
            </Box>
          </Box>
        );
      case 3:
        return (
          <Box
            sx={{
              width: '100%',
              maxWidth: hasBothSpeechInterests ? 920 : 560,
              display: 'grid',
              gridTemplateColumns: hasBothSpeechInterests ? 'repeat(2, minmax(0, 1fr))' : '1fr',
              gap: 2,
              alignItems: 'stretch'
            }}
          >
            {freeCredits.map((credit) => (
              <Card
                key={credit.id}
                elevation={0}
                sx={{
                  width: '100%',
                  borderRadius: 3,
                  textAlign: 'center',
                  background: credit.background,
                  border: `2px solid ${credit.borderColor}`,
                }}
              >
                <CardContent sx={{ p: hasBothSpeechInterests ? (isMobile ? 2 : 3) : (isMobile ? 3 : 4) }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2.5 }}>
                    {credit.icon}
                    <Typography variant={hasBothSpeechInterests ? 'h6' : 'h5'} sx={{ fontWeight: 700, color: credit.titleColor }}>
                      {credit.title}
                    </Typography>
                  </Box>

                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
                    {credit.description}
                  </Typography>

                  {hasBothSpeechInterests && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleStart(credit.route)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 2,
                        px: 2,
                      }}
                    >
                      {credit.actionLabel}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        );
      default:
        return null;
    }
  };

  const getUserName = () => {
    if (user?.displayName) {
      return user.displayName.split(' ')[0];
    }
    return 'Բարեկամ';
  };

  return (
    <Dialog 
      open={open} 
      fullScreen={fullScreen} 
      fullWidth 
      maxWidth="md"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          minHeight: fullScreen ? '100dvh' : '85vh',
          maxHeight: fullScreen ? '100dvh' : '85vh',
          m: fullScreen ? 0 : 2,
        }
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          color: 'white',
          p: isMobile ? 3 : 4,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          }} />
          
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {user && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Բարև {getUserName()} 👋
                </Typography>
              </Box>
            )}
            
            <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, mb: 1 }}>
              {currentStep === 0 ? 'Բարի գալուստ Xosum.am' : stepTitles[currentStep]}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
              {stepSubtitles[currentStep]}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
              {Array.from({ length: totalSteps }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: index <= currentStep ? 'white' : 'rgba(255,255,255,0.3)',
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ flex: 1, p: isMobile ? 3 : 4, overflowY: 'auto', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: '100%', position: 'relative', minHeight: '300px' }}>
            <Slide
              direction={slideDirection}
              in={true}
              key={currentStep}
              mountOnEnter
              unmountOnExit
              timeout={400}
            >
              <Box sx={{ 
                position: 'absolute', 
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '300px'
              }}>
                {renderStep()}
              </Box>
            </Slide>
          </Box>
        </Box>

        <Box sx={{ 
          p: isMobile ? 3 : 4, 
          pt: 2,
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(135deg, ${theme.palette.grey[50]}, ${theme.palette.grey[100]})`,
        }}>
          <IconButton 
            onClick={handleBack}
            disabled={currentStep === 0}
            sx={{ 
              visibility: currentStep === 0 ? 'hidden' : 'visible',
              width: 48,
              height: 48,
              background: 'rgba(255,255,255,0.8)',
              '&:hover': {
                background: 'rgba(255,255,255,1)',
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            {currentStep < totalSteps - 1 ? (
              <Button
                variant="contained"
                size="large"
                onClick={handleNext}
                disabled={!canProceed()}
                endIcon={<ArrowForwardIcon />}
                sx={{ 
                  minWidth: 200,
                  height: 48,
                  borderRadius: 3,
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  display: (showOtherProfessionInput || showOtherSourceInput || currentStep === 2) ? 'flex' : 'none',
                  textTransform: 'none',
                }}
              >
                Հաջորդը
              </Button>
            ) : (
              hasBothSpeechInterests ? (
                <Box sx={{ height: 56 }} />
              ) : (
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => handleStart()}
                  startIcon={<PlayArrowIcon />}
                  sx={{
                    minWidth: 240,
                    height: 56,
                    borderRadius: 3,
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    textTransform: 'none',
                    boxShadow: theme.shadows[4],
                    '&:hover': {
                      boxShadow: theme.shadows[8],
                    }
                  }}
                >
                  Սկսել
                </Button>
              )
            )}
          </Box>

          <Box sx={{ width: 48 }} />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
